import React, { useEffect, useState } from 'react';
import { Table, Tag, Button, message, Tabs, Space, Progress, Spin, Descriptions, Row, Col, Statistic, Typography } from 'antd';
import { SafetyCertificateOutlined, CloseCircleOutlined, StarFilled, TrophyOutlined } from '@ant-design/icons';
import API from '../api';

const { Text } = Typography;

interface PerfStats {
  rating: number; totalReviews: number; ratingBreakdown: Record<number, number>;
  completedJobs: number; cancelledJobs: number; totalJobs: number;
  successRate: number; avgJobValue: number; onTimeRate: number; wouldRecommendRate: number;
}

const PerformancePanel = ({ providerId }: { providerId: number }) => {
  const [stats, setStats] = useState<PerfStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/admin/providers/${providerId}/performance`)
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [providerId]);

  if (loading) return <Spin size="small" style={{ padding: 16 }} />;
  if (!stats) return <Text type="secondary">No performance data</Text>;

  const maxCount = Math.max(1, ...Object.values(stats.ratingBreakdown));

  return (
    <div style={{ padding: '8px 16px', background: '#fafafa', borderRadius: 8 }}>
      <Row gutter={24} style={{ marginBottom: 16 }}>
        <Col span={4}><Statistic title="Rating" value={stats.rating.toFixed(1)} prefix={<StarFilled style={{ color: '#faad14' }} />} /></Col>
        <Col span={4}><Statistic title="Reviews" value={stats.totalReviews} /></Col>
        <Col span={4}><Statistic title="Completed" value={stats.completedJobs} /></Col>
        <Col span={4}><Statistic title="Cancelled" value={stats.cancelledJobs} /></Col>
        <Col span={4}><Statistic title="Avg Job Value" value={`R${stats.avgJobValue.toFixed(0)}`} /></Col>
        <Col span={4}><Statistic title="Total Jobs" value={stats.totalJobs} /></Col>
      </Row>
      <Row gutter={24}>
        <Col span={8}>
          <div style={{ marginBottom: 6 }}><Text strong>Success Rate</Text></div>
          <Progress percent={stats.successRate} strokeColor="#52c41a" size="small" />
          <div style={{ marginBottom: 6, marginTop: 12 }}><Text strong>On-Time Arrival</Text></div>
          <Progress percent={stats.onTimeRate} strokeColor="#1677ff" size="small" />
          <div style={{ marginBottom: 6, marginTop: 12 }}><Text strong>Would Recommend</Text></div>
          <Progress percent={stats.wouldRecommendRate} strokeColor="#722ed1" size="small" />
        </Col>
        <Col span={8}>
          <div style={{ marginBottom: 6 }}><Text strong>Star Breakdown</Text></div>
          {[5, 4, 3, 2, 1].map(star => (
            <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ width: 28, fontSize: 12 }}>{star} ★</span>
              <Progress
                percent={Math.round((stats.ratingBreakdown[star] ?? 0) / maxCount * 100)}
                showInfo={false}
                strokeColor="#faad14"
                size="small"
                style={{ flex: 1 }}
              />
              <span style={{ width: 24, fontSize: 12, textAlign: 'right' }}>{stats.ratingBreakdown[star] ?? 0}</span>
            </div>
          ))}
        </Col>
      </Row>
    </div>
  );
};

export default function Providers() {
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');

  const load = () => {
    setLoading(true);
    const req = tab === 'pending' ? API.get('/providers/pending') : API.get('/providers');
    req.then(r => setProviders(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab]);

  const verify = (id: number) => {
    API.patch(`/providers/${id}/verify`).then(() => { message.success('Provider verified ✓'); load(); }).catch(() => message.error('Failed'));
  };

  const revoke = (id: number) => {
    API.patch(`/providers/${id}/revoke`).then(() => { message.warning('Verification revoked'); load(); }).catch(() => message.error('Failed'));
  };

  const toggleFeatured = (id: number, featured: boolean) => {
    API.patch(`/providers/${id}/feature?featured=${featured}`).then(() => {
      message.success(featured ? 'Provider featured ⭐' : 'Provider unfeatured');
      load();
    }).catch(() => message.error('Failed'));
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: 'User ID', dataIndex: 'userId', width: 80 },
    { title: 'Bio', dataIndex: 'bio', ellipsis: true },
    { title: 'Rate', dataIndex: 'hourlyRate', render: (v: number) => `R${v}/hr` },
    { title: 'Rating', dataIndex: 'rating', render: (v: number) => v ? `⭐ ${v.toFixed(1)}` : '—' },
    { title: 'Jobs', dataIndex: 'completedJobs' },
    { title: 'Location', dataIndex: 'location', ellipsis: true },
    { title: 'Verified', dataIndex: 'isVerified', render: (v: boolean) => <Tag color={v ? 'green' : 'orange'}>{v ? 'Verified' : 'Pending'}</Tag> },
    { title: 'Available', dataIndex: 'isAvailable', render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? 'Online' : 'Offline'}</Tag> },
    { title: 'Featured', dataIndex: 'isFeatured', render: (v: boolean) => v ? <Tag color='gold'>⭐ Sponsored</Tag> : <Tag>No</Tag> },
    {
      title: 'Actions', render: (_: any, r: any) => (
        <Space>
          {!r.isVerified
            ? <Button size="small" type="primary" icon={<SafetyCertificateOutlined />} onClick={() => verify(r.id)}>Verify</Button>
            : <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => revoke(r.id)}>Revoke</Button>
          }
          {!r.isFeatured
            ? <Button size="small" style={{ borderColor: '#d97706', color: '#d97706' }} onClick={() => toggleFeatured(r.id, true)}>Feature</Button>
            : <Button size="small" onClick={() => toggleFeatured(r.id, false)}>Unfeature</Button>
          }
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Tabs activeKey={tab} onChange={setTab} items={[
        { key: 'pending', label: `Pending Verification` },
        { key: 'all', label: 'All Providers' },
      ]} />
      <Table
        dataSource={providers}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 15 }}
        expandable={{
          expandedRowRender: (record: any) => <PerformancePanel providerId={record.id} />,
          expandRowByClick: false,
          rowExpandable: () => true,
        }}
      />
    </div>
  );
}
